import { useEffect, useRef, useState } from 'react'

import { t } from '../../../common/i18n.js'
import { Language } from '../../../common/types/i18n.js'
import { Button } from '../button/button.js'
import { Select } from '../forms/select/select.js'
import { ArrowUpDown, Check, Eye, SlidersHorizontal } from '../icon/solar-icons.js'
import './portal-people-filters.css'
import {
    countActiveFilters,
    type EmployeeSortField,
    type FilterOptions,
    type PeopleFilters,
    type SortState,
} from './types.js'

/** Shared click-outside popover shell. */
const usePopover = () => {
    const [open, setOpen] = useState(false)
    const ref = useRef<HTMLDivElement>(null)

    useEffect(() => {
        if (!open) return
        const onDown = (e: MouseEvent) => {
            if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
        }
        document.addEventListener('mousedown', onDown)
        return () => document.removeEventListener('mousedown', onDown)
    }, [open])

    return { open, setOpen, ref }
}

interface FilterRow {
    key: keyof PeopleFilters
    label: string
    placeholder: string
    options: { value: string; label: string }[]
    isNumber: boolean
}

export interface FilterPopoverProps {
    lang: Language
    filters: PeopleFilters
    options: FilterOptions
    onChange: (filters: PeopleFilters) => void
}

export const FilterPopover = ({ lang, filters, options, onChange }: FilterPopoverProps) => {
    const ph = t.portal.people
    const { open, setOpen, ref } = usePopover()
    const activeCount = countActiveFilters(filters)

    const rows: FilterRow[] = [
        {
            key: 'companyId',
            label: ph.company(lang),
            placeholder: ph.allCompanies(lang),
            options: options.companies,
            isNumber: true,
        },
        {
            key: 'teamId',
            label: ph.team(lang),
            placeholder: ph.allTeams(lang),
            options: options.teams,
            isNumber: true,
        },
        {
            key: 'locationId',
            label: ph.placeOfWork(lang),
            placeholder: ph.allLocations(lang),
            options: options.locations,
            isNumber: true,
        },
        {
            key: 'countryCode',
            label: ph.country(lang),
            placeholder: ph.allCountries(lang),
            options: options.countries,
            isNumber: false,
        },
        {
            key: 'managerId',
            label: ph.manager(lang),
            placeholder: ph.allManagers(lang),
            options: options.managers,
            isNumber: true,
        },
    ]

    const setValue = (row: FilterRow, raw: string) => {
        const next: PeopleFilters = { ...filters }
        if (!raw) {
            delete next[row.key]
        } else if (row.isNumber) {
            ;(next[row.key] as number) = Number(raw)
        } else {
            ;(next[row.key] as string) = raw
        }
        onChange(next)
    }

    return (
        <div className="portal-filters__popover" ref={ref}>
            <Button
                variant="secondary"
                size="small"
                iconLeft={SlidersHorizontal}
                text={ph.filterBtn(lang)}
                onClick={() => setOpen((v) => !v)}
            />
            {activeCount > 0 && <span className="portal-filters__badge">{activeCount}</span>}
            {open && (
                <div className="portal-filters__panel">
                    {rows
                        // Hide a filter with nothing meaningful to pick — a single
                        // option (or none) leaves no real choice (e.g. one country).
                        // Keep it if it currently holds an active value, so it can
                        // still be cleared.
                        .filter((row) => row.options.length > 1 || filters[row.key] !== undefined)
                        .map((row) => (
                            <Select
                                key={String(row.key)}
                                wrapperClass="portal-filters__field"
                                label={row.label}
                                placeholder={row.placeholder}
                                value={String(filters[row.key] ?? '')}
                                onChange={(value) => setValue(row, value)}
                                options={row.options}
                            />
                        ))}
                    {activeCount > 0 && (
                        <button
                            type="button"
                            className="portal-filters__clear"
                            onClick={() => onChange({})}
                        >
                            {ph.clearAll(lang)}
                        </button>
                    )}
                </div>
            )}
        </div>
    )
}

export interface ViewToggle {
    key: string
    label: string
    active: boolean
    onToggle: () => void
}

export interface ViewPopoverProps {
    /** Button label (e.g. "Vaade" / "View"). */
    label: string
    /** On/off toggles shown in the panel; each row shows a check when active. */
    toggles: ViewToggle[]
}

// A small "view options" popover — boolean toggles (e.g. show/hide a column or
// an overlay). Mirrors FilterPopover/SortPopover; the panel stays open so
// several toggles can be flipped in one go.
export const ViewPopover = ({ label, toggles }: ViewPopoverProps) => {
    const { open, setOpen, ref } = usePopover()

    return (
        <div className="portal-filters__popover" ref={ref}>
            <Button
                variant="secondary"
                size="small"
                iconLeft={Eye}
                text={label}
                onClick={() => setOpen((v) => !v)}
            />
            {open && (
                <div className="portal-filters__panel">
                    {toggles.map((tg) => (
                        <button
                            key={tg.key}
                            type="button"
                            className="portal-filters__item"
                            onClick={tg.onToggle}
                        >
                            <span>{tg.label}</span>
                            {tg.active && <Check size={14} />}
                        </button>
                    ))}
                </div>
            )}
        </div>
    )
}

export interface SortPopoverProps {
    lang: Language
    sort: SortState
    onChange: (sort: SortState) => void
    /** Which sort fields to show. Defaults to name + hire-date (no leave_type). */
    fields?: EmployeeSortField[]
}

const ALL_SORT_OPTIONS: { field: EmployeeSortField; dir: 'asc' | 'desc' }[] = [
    { field: 'first_name', dir: 'asc' },
    { field: 'last_name', dir: 'asc' },
    { field: 'hire_date', dir: 'desc' },
    { field: 'leave_type', dir: 'asc' },
]

const DEFAULT_SORT_FIELDS: EmployeeSortField[] = ['first_name', 'last_name', 'hire_date']

const SORT_LABELS: Record<EmployeeSortField, (lang: Language) => string> = {
    first_name: (lang) => t.portal.people.sortFirstName(lang),
    last_name: (lang) => t.portal.people.sortLastName(lang),
    hire_date: (lang) => t.portal.people.sortStartDate(lang),
    leave_type: (lang) => t.portal.people.sortLeaveType(lang),
}

export const SortPopover = ({ lang, sort, onChange, fields }: SortPopoverProps) => {
    const ph = t.portal.people
    const { open, setOpen, ref } = usePopover()

    const allowedFields = new Set(fields ?? DEFAULT_SORT_FIELDS)
    const sortOptions = ALL_SORT_OPTIONS.filter((o) => allowedFields.has(o.field)).map((o) => ({
        ...o,
        label: SORT_LABELS[o.field](lang),
    }))

    return (
        <div className="portal-filters__popover" ref={ref}>
            <Button
                variant="secondary"
                size="small"
                iconLeft={ArrowUpDown}
                text={ph.sortBtn(lang)}
                onClick={() => setOpen((v) => !v)}
            />
            {open && (
                <div className="portal-filters__panel">
                    {sortOptions.map((o) => (
                        <button
                            key={o.field}
                            type="button"
                            className="portal-filters__item"
                            onClick={() => {
                                onChange({ field: o.field, dir: o.dir })
                                setOpen(false)
                            }}
                        >
                            <span>{o.label}</span>
                            {sort.field === o.field && <Check size={14} />}
                        </button>
                    ))}
                </div>
            )}
        </div>
    )
}
